import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import type { IconComponent } from '../lib/types';

export interface EmptyStateProps {
  icon?: IconComponent | undefined;
  title: string;
  /** Why it is empty and what to do about it. "No results" alone tells the
   *  user nothing they did not already know. */
  description?: string | undefined;
  action?: ReactNode;
  className?: string | undefined;
}

/**
 * The "nothing here yet" state, required for every list by the Definition of
 * Done in planning.md §6 — distinct from loading (skeletons) and from failure
 * (`Callout`). Conflating the three is how an empty list ends up looking like
 * a broken one.
 */
export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2 px-6 py-10 text-center',
        className,
      )}
    >
      {Icon === undefined ? null : <Icon aria-hidden className="size-8 text-fg-subtle" />}
      <p className="text-sm font-medium text-fg">{title}</p>
      {description === undefined ? null : (
        <p className="max-w-prose text-sm text-fg-muted">{description}</p>
      )}
      {action === undefined ? null : <div className="mt-2">{action}</div>}
    </div>
  );
}
